import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Modal, Row, Tooltip } from "antd";
import { join, slice } from "lodash";
import { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import apiFactory from "../../api";
import { useInfoUser } from "../../store/UserStore";
import { openNotificationError } from "../../utils/Utils";
import { CustomAvatar } from "../avatar/CustomAvatar";
import { GeneralModal } from "./GeneralModal";
import "./style.scss";

const MemberProjectModal = ({
  isOpen,
  onClose,
  selectedTask,
  onCreate,
  onRemove,
  memberList,
  type,
  permission,
}) => {
  const [members, setMembers] = useState([]);
  const [newSelectedMembers, setNewSelectedMembers] = useState([]);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const { languageMap } = useInfoUser();

  const fetchMembers = async () => {
    try {
      if (!selectedTask) {
        return;
      }

      switch (type) {
        case "CREATE":
          const existedMembers =
            selectedTask?.taskMemberDtoList?.map(
              (member) => member?.memberCode
            ) || null;

          const existedProjectMembers =
            await apiFactory.taskApi.getListMemberProject({
              projectCode: selectedTask?.projectCode,
              memberCodes: existedMembers,
            });

          setMembers(existedProjectMembers?.data);
          break;
        case "REMOVE":
          const existedTaskMembers =
            await apiFactory.taskMemberApi.getListMemberTask(
              selectedTask?.taskCode
            );

          setMembers(existedTaskMembers?.data);
          break;
        case "MENTION":
          setMembers(memberList);
          break;
        default:
          break;
      }
    } catch (error) {
      openNotificationError(error?.message);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [isOpen]);

  const handleCheckboxChange = (value) => {
    if (value === null) {
      setNewSelectedMembers((prev) => {
        if (prev?.length < members?.length) {
          return members?.map((member) => member?.userCode);
        } else {
          return [];
        }
      });

      return;
    }

    setNewSelectedMembers((prev) => {
      if (prev?.includes(value)) {
        return prev?.filter((code) => code !== value);
      } else {
        if (prev) {
          return [...prev, value];
        } else {
          return [value];
        }
      }
    });
  };

  const handleSubmitAddMember = () => {
    const memberRequestList = newSelectedMembers?.map((code) => ({
      code: code,
      sortSequence: "desc",
    }));

    onCreate(memberRequestList);
  };

  const handleSubmitRemoveMember = () => {
    const memberRequestList = newSelectedMembers?.map((code) => ({
      code: code,
      sortSequence: "desc",
    }));
    onRemove(memberRequestList);
  };

  const handleSubmitMentionMember = () => {
    onCreate(newSelectedMembers);
  };

  const handleOnClick = () => {
    switch (type) {
      case "CREATE":
        return handleSubmitAddMember();
      case "REMOVE":
        setIsOpenDeleteModal(true);
        break;
      case "MENTION":
        handleSubmitMentionMember();
        break;
      default:
        return handleSubmitAddMember();
    }
  };

  const handleTitle = () => {
    switch (type) {
      case "CREATE":
        return (
          <div className="pb-2">
            <span>
              {languageMap?.["project.task.assignee.modalAdd"] ?? "Add members"}
            </span>
            <hr className="mt-2" />
          </div>
        );
      case "REMOVE":
        return (
          <div className="pb-2">
            <span>
              {languageMap?.["project.task.assignee.modalRemove"] ?? "Assignee"}
            </span>
            <hr className="mt-2" />
          </div>
        );
      default:
        return (
          <div className="pb-2">
            <span>Add members</span>
            <hr className="mt-2" />
          </div>
        );
    }
  };

  const handleButtonFooter = () => {
    switch (type) {
      case "CREATE":
        return (
          <div className="h-[40px]">
            <hr className="mb-2" />
            {members?.length > 0 && (
              <Button
                disabled={newSelectedMembers?.length === 0}
                icon={<PlusCircleOutlined />}
                onClick={handleOnClick}
              >
                {languageMap?.["project.task.assignee.confirmBtn"] ?? "Confirm"}
              </Button>
            )}
          </div>
        );
      case "REMOVE":
        return (
          <div className="h-[40px]">
            <hr className="mb-2" />
            {newSelectedMembers?.length > 0 && (
              <Button
                disabled={!permission?.includes("U")}
                icon={<MdDelete size={20} color="red" />}
                onClick={handleOnClick}
              ></Button>
            )}
          </div>
        );
      default:
        return (
          <div className="h-[40px]">
            <hr className="mb-2" />
            {members?.length > 0 && (
              <Button
                disabled={newSelectedMembers?.length === 0}
                icon={<PlusCircleOutlined />}
                onClick={handleOnClick}
              >
                {languageMap?.["project.task.assignee.confirmBtn"] ?? "Confirm"}
              </Button>
            )}
          </div>
        );
    }
  };

  const generatePath = (user, childPaths, index) => {
    const pathName = join(
      slice(childPaths, 0, user?.paths?.[index].length)?.map(
        (path) => path?.name
      ),
      ">"
    );

    return (
      <div key={`${user?.userId}_${pathName}`}>
        <span className="text-[#d33b3d] p-[2px]">*</span>
        <span style={{ fontSize: `12px` }}>{pathName}</span>
      </div>
    );
  };

  return (
    <>
      <Modal
        title={handleTitle()}
        open={isOpen}
        onCancel={onClose}
        footer={handleButtonFooter()}
        width={712}
        centered
      >
        <div className="overflow-y-auto h-[400px]">
          <Row className="flex items-center justify-center mb-3">
            <Col span={1}>
              <Tooltip
                placement="top"
                title={languageMap?.["event.dashboard.myProject.all"] ?? "All"}
                color={"#0091ff"}
              >
                <Checkbox
                  checked={
                    members?.length === newSelectedMembers?.length &&
                    newSelectedMembers?.length !== 0
                  }
                  onChange={() => handleCheckboxChange(null)}
                />
              </Tooltip>
            </Col>
            <Col span={3} />
            <Col span={6}>{languageMap?.["menu.profile.name"] ?? "Name"}</Col>
            <Col span={14} className="flex items-center justify-center">
              {languageMap?.["modal.organization.organization"] ??
                "Organization"}
            </Col>
          </Row>
          <hr className="mb-3" />
          {members?.length > 0 ? (
            members?.map((user) => (
              <Row key={user?.userId} className="flex items-center mb-3">
                <Col span={1}>
                  <Checkbox
                    checked={newSelectedMembers?.some(
                      (selectedMember) => selectedMember === user?.userCode
                    )}
                    onChange={() => handleCheckboxChange(user?.userCode)}
                  />
                </Col>
                <Col span={3} className="flex items-center justify-center">
                  <CustomAvatar person={user} pos={-1} styleCss={{}} />
                </Col>
                <Col span={6}>
                  <span className="text-xs">{user?.name}</span>
                </Col>
                <Col span={14}>
                  {user?.paths?.length > 0 ? (
                    user?.paths?.map((childPaths, index) =>
                      generatePath(user, childPaths, index)
                    )
                  ) : (
                    <div>
                      <span className="text-[#d33b3d] p-[2px]">*</span>
                      <span style={{ fontSize: `12px` }}>UNKNOWN GROUP</span>
                    </div>
                  )}
                </Col>
              </Row>
            ))
          ) : (
            <Row>
              <Col span={24} className="flex items-center justify-center mb-2">
                <span>No Data found</span>
              </Col>
            </Row>
          )}
        </div>
      </Modal>
      {isOpenDeleteModal && (
        <GeneralModal
          title={
            languageMap?.["project.task.assignee.modalRemoveYn.title"] ??
            "Do you want to remove member"
          }
          onCancel={() => setIsOpenDeleteModal(false)}
          open={isOpenDeleteModal}
          onConfirm={handleSubmitRemoveMember}
        />
      )}
    </>
  );
};

export { MemberProjectModal };
